import { applyPadel24Result } from '../engine/padel24Results';
import { applyAmericanoResult } from '../engine/americanoResults';
import { applyKMResult, warnOnKMIssue } from '../engine/kmResults';
import { AMERICANO_FORMATS } from '../engine/constants';

// ─────────────────────────────────────────────────────────────────────
// useTournamentResultRecording — the four pad-driven result/live-score writers
// for the ACTIVE or a PAUSED tournament, extracted VERBATIM from
// useTournamentLifecycle (PR-BF) so they can be shared by:
//   • useTournamentLifecycle (the admin app), and
//   • the physical Banepad device route (useBanepadPadActions).
//
// It is deliberately EFFECT-FREE (pure handler factory) — unlike
// useTournamentLifecycle it carries none of the lifecycle effects, so it is safe
// to mount in the standalone device tree (a kiosk must record results but must
// NOT run tournament-lifecycle side effects). The actual result math stays in the
// shared engine appliers (applyPadel24Result / applyAmericanoResult /
// applyKMResult) — only the find-active-or-paused + persist wrapper lives here.
//
// `persistPaused` is injected (the sole writer of paused_tournaments_v1 lives at
// the call site) so this hook adds no new Storage writer.
// ─────────────────────────────────────────────────────────────────────
export interface UseTournamentResultRecordingDeps {
  tournament: any;
  setTournament: (updater: any) => void;
  setPausedTournaments: (updater: any) => void;
  effectivePlayerDB: any;
  /** Storage-first writer for the paused-tournaments list (paused_tournaments_v1). */
  persistPaused: (list: any[]) => void | Promise<void>;
}

export function useTournamentResultRecording({
  tournament,
  setTournament,
  setPausedTournaments,
  effectivePlayerDB,
  persistPaused,
}: UseTournamentResultRecordingDeps) {
  const recordResultForTournament = (
    tid: any,
    matchId: any,
    score1: any,
    score2: any,
    source: any,
  ) => {
    // Pick the right result logic based on format: Padel24 vs the Americano family.
    const applyByType = (t: any) => {
      const type = t.type || 'padel24';
      if (type === 'padel24') return applyPadel24Result(t, matchId, score1, score2, source);
      if (AMERICANO_FORMATS.includes(type))
        return applyAmericanoResult(t, matchId, score1, score2, effectivePlayerDB);
      return t;
    };
    if (tournament && (tournament.id === tid || tournament.displayId === tid)) {
      if (tournament.status === 'ready') return;
      const type = tournament.type || 'padel24';
      if (type !== 'padel24' && !AMERICANO_FORMATS.includes(type)) return;
      setTournament((s: any) => applyByType(s));
      return;
    }
    setPausedTournaments((prev: any[]) => {
      const next = prev.map((t) => {
        if ((t.id || t.displayId) !== tid || t.status === 'ready') return t;
        const type = t.type || 'padel24';
        if (type !== 'padel24' && !AMERICANO_FORMATS.includes(type)) return t;
        return applyByType(t);
      });
      persistPaused(next);
      return next;
    });
  };

  const recordKMResultForTournament = (tid: any, updatedMatch: any) => {
    if (tournament && (tournament.id === tid || tournament.displayId === tid)) {
      if (tournament.status === 'ready' || tournament.type !== 'klubbmesterskap') return;
      // DEGRADE, deliberately (#204). applyKMResult now throws by default, so
      // that a server-side caller cannot mistake a half-applied result for a
      // good one. In the browser a person is looking at the board mid-tournament
      // and a warning beats a blank screen, so this call-site opts out — which
      // is a decision made here rather than a default inherited from the engine.
      setTournament((s: any) =>
        applyKMResult(s, updatedMatch, effectivePlayerDB, { onIssue: warnOnKMIssue }),
      );
      return;
    }
    setPausedTournaments((prev: any[]) => {
      const next = prev.map((t) =>
        (t.id || t.displayId) === tid && t.status !== 'ready' && t.type === 'klubbmesterskap'
          ? applyKMResult(t, updatedMatch, effectivePlayerDB, { onIssue: warnOnKMIssue })
          : t,
      );
      persistPaused(next);
      return next;
    });
  };

  // Persist a KM match's IN-PROGRESS score (liveScore) continuously, WITHOUT
  // finalising it (no ELO, no result). The match stays unplayed until the real
  // recordKMResultForTournament runs on «Send resultat».
  const recordKMLiveScore = (tid: any, updatedMatch: any) => {
    const applyLive = (t: any) => ({
      ...t,
      matches: (t.matches || []).map((m: any) =>
        m.id === updatedMatch.id ? { ...m, liveScore: updatedMatch.liveScore } : m,
      ),
    });
    if (tournament && (tournament.id === tid || tournament.displayId === tid)) {
      if (tournament.status === 'ready') return;
      setTournament((s: any) => applyLive(s));
      return;
    }
    setPausedTournaments((prev: any[]) => {
      const next = prev.map((t) => ((t.id || t.displayId) === tid ? applyLive(t) : t));
      persistPaused(next);
      return next;
    });
  };

  // Padel24/Americano live (in-progress) points — persist match.liveScore by id
  // without finalising (no ELO, no result). Same model as recordKMLiveScore but
  // for the points-based CourtPadCard formats; liveScore = { score1, score2 }.
  const recordPadLiveScore = (tid: any, matchId: any, liveScore: any) => {
    const applyLive = (t: any) => ({
      ...t,
      matches: (t.matches || []).map((m: any) => (m.id === matchId ? { ...m, liveScore } : m)),
    });
    if (tournament && (tournament.id === tid || tournament.displayId === tid)) {
      if (tournament.status === 'ready') return;
      setTournament((s: any) => applyLive(s));
      return;
    }
    setPausedTournaments((prev: any[]) => {
      const next = prev.map((t) => ((t.id || t.displayId) === tid ? applyLive(t) : t));
      persistPaused(next);
      return next;
    });
  };

  return {
    recordResultForTournament,
    recordKMResultForTournament,
    recordKMLiveScore,
    recordPadLiveScore,
  };
}
